<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { vInfiniteScroll } from '@vueuse/components';
  import { computed, ref, useId, useSlots, watch } from 'vue';
  import { useSelectAll } from '../../_shared/composables/useSelectAll';
  import { Dropdown } from '../../Dropdown';
  import { dropdownPanelDividerClass } from '../../Dropdown/Dropdown.css';
  import { DropdownApplyFooter } from '../../Dropdown/DropdownApplyFooter';
  import { DropdownPanelScroll } from '../../Dropdown/DropdownPanelScroll';
  import { LoaderContainer } from '../../Loader';
  import { FieldSize, FormField } from '../FormField';
  import {
    optionSectionClass,
    paginationLoaderClass,
    panelMessageClass,
    selectPanelClass,
  } from './Select.css';
  import {
    canCreateFromQuery,
    filterOptions,
    flattenGroups,
    resolveOptionDisabled,
    resolveOptionLabel,
    resolveOptionValue,
    type SelectFlatRow,
    toSafeId,
  } from './Select-utils';
  import {
    SelectCommit,
    type SelectCommitType,
    type SelectEmits,
    type SelectOption,
    SelectOptionsAs,
    type SelectOptionsAsType,
    type SelectNavTarget,
    type SelectProps,
    SelectSelection,
  } from './Select.types';
  import SelectOptionRow from './parts/SelectOptionRow.vue';
  import SelectTrigger from './parts/SelectTrigger.vue';

  const props = withDefaults(defineProps<SelectProps>(), {
    selection: SelectSelection.Single,
    internalSearch: true,
    allowEmpty: true,
    disabled: false,
    loading: false,
    searchable: false,
    withSelectAll: false,
    groupSelect: false,
    disableTeleport: false,
    size: FieldSize.MD,
  });

  const emit = defineEmits<SelectEmits>();

  if (props.creatable && !props.searchable) {
    throw new Error(
      'DEV ERROR: Select with creatable requires searchable to be true',
    );
  }

  const { t } = useLocale('ds-components');
  const slots = useSlots();

  const uid = useId();
  const fieldId = computed(() => props.id ?? `ds-select-${uid}`);
  const panelId = computed(() => `${fieldId.value}-panel`);

  const isMultiple = computed(
    () => props.selection === SelectSelection.Multiple,
  );

  const resolvedCommit = computed<SelectCommitType>(
    () =>
      props.commit ??
      (isMultiple.value ? SelectCommit.Apply : SelectCommit.Immediate),
  );

  const isDeferred = computed(
    () => resolvedCommit.value === SelectCommit.Apply,
  );

  const resolvedOptionsAs = computed<SelectOptionsAsType>(
    () =>
      props.optionsAs ??
      (isMultiple.value ? SelectOptionsAs.Checkbox : SelectOptionsAs.Plain),
  );

  const labelOf = (option: SelectOption) =>
    resolveOptionLabel(option, props.optionLabel);

  const keyOf = (option: SelectOption) =>
    resolveOptionValue(option, props.optionValue);

  const disabledOf = (option: SelectOption) =>
    resolveOptionDisabled(option, props.optionDisabled);

  const modelSelection = computed<SelectOption[]>(() => {
    const value = props.modelValue;
    if (value === null || value === undefined) return [];
    return Array.isArray(value) ? value : [value];
  });

  const staged = ref<SelectOption[]>([...modelSelection.value]);

  const isOpen = ref(false);
  const query = ref('');

  const resetStaging = () => {
    staged.value = [...modelSelection.value];
  };

  const activeIndex = ref(-1);

  const triggerEl = ref<InstanceType<typeof SelectTrigger> | null>(null);

  const initialFocusEl = () =>
    props.searchable
      ? (triggerEl.value?.searchInputEl() ?? null)
      : (triggerEl.value?.comboboxEl() ?? null);

  const onOpenChange = (open: boolean) => {
    isOpen.value = open;
    activeIndex.value = -1;
    if (open) {
      resetStaging();
    } else {
      query.value = '';
      resetStaging();
    }
    emit('update:open', open);
  };

  watch(modelSelection, resetStaging);

  watch(
    () => props.disabled,
    (disabled) => {
      if (disabled) isOpen.value = false;
    },
  );

  const currentSelection = computed<SelectOption[]>(() =>
    isDeferred.value ? staged.value : modelSelection.value,
  );

  const displayValue = computed(() =>
    modelSelection.value.map(labelOf).join(', '),
  );

  const isSelected = (option: SelectOption) =>
    currentSelection.value.some(
      (selected) => keyOf(selected) === keyOf(option),
    );

  const setSelection = (next: SelectOption[]) => {
    if (isDeferred.value) {
      staged.value = next;
      return;
    }
    emit('update:modelValue', isMultiple.value ? next : (next[0] ?? null));
  };

  const searchedOptions = computed(() =>
    props.internalSearch
      ? filterOptions(props.options, query.value, props.optionLabel)
      : props.options,
  );

  const rows = computed(() =>
    flattenGroups(
      searchedOptions.value,
      props.groupValues,
      props.groupLabel,
      props.optionLabel,
    ),
  );

  type SelectSection = {
    key: string;
    group?: Extract<SelectFlatRow, { kind: 'group' }>;
    options: Extract<SelectFlatRow, { kind: 'option' }>[];
  };

  const sections = computed<SelectSection[]>(() => {
    const result: SelectSection[] = [];
    for (const row of rows.value) {
      if (row.kind === 'group') {
        result.push({
          key: `group-${keyOf(row.option)}`,
          group: row,
          options: [],
        });
        continue;
      }
      const last = result.at(-1);
      if (last) {
        last.options.push(row);
      } else {
        result.push({ key: 'options', options: [row] });
      }
    }
    return result;
  });

  const flatOptions = computed(() =>
    rows.value.filter((row) => row.kind === 'option').map((row) => row.option),
  );

  const { allSelected, someSelected, selectableOptions } =
    useSelectAll<SelectOption>({
      options: () => flatOptions.value,
      selected: () => currentSelection.value,
      isSelected,
      isDisabled: disabledOf,
    });

  const selectAllLabel = computed(() =>
    allSelected.value ? t('select.deselect_all') : t('select.select_all'),
  );

  const showSelectAll = computed(() => props.withSelectAll && isMultiple.value);

  const groupChildren = (group: SelectOption) =>
    props.groupValues && typeof group === 'object' && group !== null
      ? ((group[props.groupValues] as SelectOption[]) ?? [])
      : [];

  const rowTestId = (option: SelectOption) => `select-option-${keyOf(option)}`;

  const rowId = (option: SelectOption) =>
    `${fieldId.value}-option-${toSafeId(keyOf(option))}`;

  const groupRowId = (group: SelectOption) =>
    `${fieldId.value}-group-${toSafeId(keyOf(group))}`;

  const createRowId = computed(() => `${fieldId.value}-create`);

  const selectAllRowId = computed(() => `${fieldId.value}-select-all`);

  const showCreateRow = computed(
    () =>
      Boolean(props.creatable) &&
      canCreateFromQuery(
        query.value,
        props.options,
        props.optionLabel,
        props.createValidator,
      ),
  );

  const createRowLabel = computed(
    () =>
      props.createLabel ??
      t('select.create_option', { query: query.value.trim() }),
  );

  const navigableTargets = computed<SelectNavTarget[]>(() => [
    ...(showSelectAll.value ? [{ kind: 'selectAll' } as const] : []),
    ...(showCreateRow.value ? [{ kind: 'create' } as const] : []),
    ...flatOptions.value
      .filter((option) => !disabledOf(option))
      .map((option) => ({ kind: 'option', option }) as const),
  ]);

  const activeTarget = computed<SelectNavTarget | undefined>(
    () => navigableTargets.value[activeIndex.value],
  );

  const activeDescendantId = computed(() => {
    const target = activeTarget.value;
    if (!target) return undefined;
    if (target.kind === 'selectAll') return selectAllRowId.value;
    return target.kind === 'create' ? createRowId.value : rowId(target.option);
  });

  const moveActive = (delta: number) => {
    const total = navigableTargets.value.length;
    if (!total) return;
    activeIndex.value =
      activeIndex.value < 0 && delta < 0
        ? total - 1
        : (activeIndex.value + delta + total) % total;
  };

  const onSelectOption = (option: SelectOption) => {
    if (!isMultiple.value) {
      setSelection([option]);
      if (!isDeferred.value) onOpenChange(false);
      return;
    }

    setSelection(
      isSelected(option)
        ? currentSelection.value.filter(
            (selected) => keyOf(selected) !== keyOf(option),
          )
        : [...currentSelection.value, option],
    );
  };

  const onSelectGroup = (group: SelectOption) => {
    const children = groupChildren(group).filter(
      (option) => !disabledOf(option),
    );
    const everySelected = children.every(isSelected);

    setSelection(
      everySelected
        ? currentSelection.value.filter(
            (selected) =>
              !children.some((child) => keyOf(child) === keyOf(selected)),
          )
        : [
            ...currentSelection.value,
            ...children.filter((child) => !isSelected(child)),
          ],
    );
  };

  const onToggleAll = () => {
    setSelection(allSelected.value ? [] : [...selectableOptions.value]);
  };

  const onRemoveOption = (option: SelectOption) => {
    const next = modelSelection.value.filter(
      (selected) => keyOf(selected) !== keyOf(option),
    );
    staged.value = next;
    emit('update:modelValue', isMultiple.value ? next : (next[0] ?? null));
  };

  const onApply = () => {
    emit(
      'update:modelValue',
      isMultiple.value ? staged.value : (staged.value[0] ?? null),
    );
    isOpen.value = false;
    query.value = '';
    emit('update:open', false);
  };

  const onCreate = () => {
    emit('create', query.value.trim());
    onQueryChange('');
  };

  const activateTarget = () => {
    const target = activeTarget.value;

    if (!target) {
      if (showCreateRow.value) onCreate();
      return;
    }

    if (target.kind === 'create') {
      onCreate();
      return;
    }

    if (target.kind === 'selectAll') {
      onToggleAll();
      return;
    }

    onSelectOption(target.option);
  };

  const hasQuery = computed(() => query.value.trim().length > 0);

  const isPanelLoading = computed(() => props.loading && isOpen.value);

  const canLoadMoreNow = computed(
    () => () => !props.paginationLoading && (props.canLoadMore?.() ?? false),
  );

  const loadMore = async () => {
    if (!canLoadMoreNow.value()) return;
    try {
      await props.onLoadMore?.();
    } catch (error) {
      emit('loadMoreError', error);
    }
  };

  defineExpose({ loadMore });

  const onKeydown = (event: KeyboardEvent) => {
    if (props.disabled) return;

    if (!isOpen.value) {
      if (props.loading) return;
      if (event.key === 'Enter' || event.key === 'ArrowDown') {
        event.preventDefault();
        onOpenChange(true);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        activeIndex.value = navigableTargets.value.length ? 0 : -1;
        break;
      case 'End':
        event.preventDefault();
        activeIndex.value = navigableTargets.value.length - 1;
        break;
      case 'Enter':
        event.preventDefault();
        activateTarget();
        break;
      case 'Escape':
        event.preventDefault();
        onOpenChange(false);
        break;
    }
  };

  const onQueryChange = (value: string) => {
    query.value = value;
    activeIndex.value = -1;
    emit('search', value);
  };
</script>

<template>
  <FormField
    :id="fieldId"
    :label="label"
    :optional-label="optionalLabel"
    :helper-text="helperText"
    :error="error"
    :warning="warning"
    :meta="meta"
    :size="size"
    :disabled-reason="disabled ? disabledReason : undefined"
  >
    <template v-if="slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>

    <template v-if="slots.labelTrailing" #labelTrailing>
      <slot name="labelTrailing" />
    </template>

    <template #default="{ describedBy, hasError, labelId }">
      <Dropdown
        :open="isOpen"
        :disabled="disabled || (loading && !isOpen)"
        :placement="placement"
        :disable-teleport="disableTeleport"
        :initial-focus-el="initialFocusEl"
        :with-padding="false"
        same-width
        self-scroll
        trigger-as-child
        trigger-full-width
        @update:open="onOpenChange"
      >
        <template #trigger>
          <SelectTrigger
            ref="triggerEl"
            :selected="modelSelection"
            :label-of="labelOf"
            :key-of="keyOf"
            :multiple="isMultiple"
            :query="query"
            :display-value="displayValue"
            :placeholder="placeholder"
            :searchable="searchable"
            :is-open="isOpen"
            :disabled="disabled"
            :loading="loading"
            :max-visible-tags="maxVisibleTags"
            :size="size"
            :field-id="fieldId"
            :panel-id="panelId"
            :label-id="labelId"
            :described-by="describedBy"
            :has-error="hasError"
            :test-id="testId"
            :active-descendant-id="activeDescendantId"
            @remove="onRemoveOption"
            @update:query="onQueryChange"
            @navigate="onKeydown"
          />
        </template>

        <div :class="selectPanelClass">
          <div
            :id="panelId"
            :class="selectPanelClass"
            role="listbox"
            :aria-label="label"
            :aria-multiselectable="isMultiple ? 'true' : undefined"
          >
            <template v-if="showSelectAll">
              <SelectOptionRow
                :row-id="selectAllRowId"
                :active="activeDescendantId === selectAllRowId"
                :label="selectAllLabel"
                :selected="allSelected"
                :indeterminate="someSelected"
                as-checkbox
                test-id="select-all"
                @select="onToggleAll"
              />
              <div :class="dropdownPanelDividerClass" role="presentation" />
            </template>

            <DropdownPanelScroll
              v-infinite-scroll="[loadMore, { canLoadMore: canLoadMoreNow }]"
            >
              <SelectOptionRow
                v-if="showCreateRow"
                :row-id="createRowId"
                :active="activeDescendantId === createRowId"
                :label="createRowLabel"
                :icon="TUNDR_ICONS.PLUS"
                test-id="select-create"
                @select="onCreate"
              />
              <div
                v-for="section in sections"
                :key="section.key"
                :class="optionSectionClass"
                :role="section.group ? 'group' : undefined"
                :aria-labelledby="
                  section.group ? groupRowId(section.group.option) : undefined
                "
              >
                <SelectOptionRow
                  v-if="section.group"
                  :row-id="groupRowId(section.group.option)"
                  :label="section.group.label"
                  is-group
                  :selectable="groupSelect"
                  :test-id="`select-group-${keyOf(section.group.option)}`"
                  @select="onSelectGroup(section.group.option)"
                />
                <SelectOptionRow
                  v-for="row in section.options"
                  :key="keyOf(row.option)"
                  :row-id="rowId(row.option)"
                  :label="labelOf(row.option)"
                  :selected="isSelected(row.option)"
                  :active="activeDescendantId === rowId(row.option)"
                  :disabled="disabledOf(row.option)"
                  :as-checkbox="resolvedOptionsAs === SelectOptionsAs.Checkbox"
                  :test-id="rowTestId(row.option)"
                  @select="onSelectOption(row.option)"
                />
              </div>

              <div
                v-if="!rows.length && !showCreateRow && !isPanelLoading"
                :class="panelMessageClass"
                data-test-id="select-panel-message"
              >
                <slot v-if="hasQuery && slots.noResults" name="noResults" />
                <slot v-else-if="!hasQuery && slots.empty" name="empty" />
                <template v-else-if="hasQuery">{{
                  noResultsMessage ?? t('select.no_results')
                }}</template>
                <template v-else>{{
                  emptyMessage ?? t('select.no_options')
                }}</template>
              </div>

              <LoaderContainer
                v-if="isPanelLoading"
                :class="paginationLoaderClass"
                data-test-id="select-panel-loader"
              />

              <LoaderContainer
                v-else-if="paginationLoading"
                :class="paginationLoaderClass"
                data-test-id="select-pagination-loader"
              />
            </DropdownPanelScroll>
          </div>

          <DropdownApplyFooter v-if="isDeferred" @apply="onApply" />
        </div>
      </Dropdown>
    </template>
  </FormField>
</template>
